import { useDisclosure, Icon, Tooltip, Box } from '@chakra-ui/react'
import { FC, ReactNode, useMemo } from 'react'
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai'
import { FaClipboardList, FaSpinner, FaClock, FaTimes } from 'react-icons/fa'
import { MdError } from 'react-icons/md'

import useAlertPurchase from '@hook/alerts/use-alert-purchase'
import useTable from '@hook/shared/use-table'

import {
  getProcessGroupStatus,
  isProcessStatusVisible
} from '@helper/alert/purchaseProcessStatus'

import TableGridItemAlertFollowUp from './item-followup'

// status -> tooltip e ícone exibidos na linha do grupo
const STATUS_CONFIG: Record<string, { label: string; icon: ReactNode }> = {
  0: {
    label: 'Iniciando',
    icon: <FaClipboardList size={20} color="#F0E68C" />
  },
  1: {
    label: 'Processando',
    icon: (
      <Icon
        as={FaSpinner}
        color="#FFA500"
        boxSize={5}
        sx={{
          animation: 'spin 1s linear infinite',
          '@keyframes spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' }
          }
        }}
      />
    )
  },
  2: {
    label: 'Concluído',
    icon: <FaClipboardList size={20} color="#3CB371" />
  },
  3: { label: 'Erro', icon: <MdError size={22} color="#E53E3E" /> },
  4: {
    label: 'Aguardando Aprovação',
    icon: <FaClock size={20} color="#F59E0B" />
  },
  5: { label: 'Recusado', icon: <FaTimes size={20} color="#DC2626" /> }
}

const TableGridCollpsePurchaseProcess: FC<{ filterDate: string }> = ({
  filterDate
}) => {
  const { data: rows } = useTable()
  const {
    atrasadoCheck,
    iniciadoCheck,
    atencaoCheck,
    noprazoCheck,
    aguardandoAprovacaoCheck,
    recusadoCheck
  } = useAlertPurchase()

  const ref = '#collapseOne'
  const { isOpen, onToggle } = useDisclosure()
  const attributes = ref ? { href: ref } : {}

  const rowsMap = useMemo(() => {
    return rows.data.filter((x: any) => x['info-id'] === filterDate)
  }, [rows, filterDate])

  const firstObject = useMemo(() => rowsMap[0], [rowsMap])
  const count = rowsMap.length

  // Status do grupo calculado com TODAS as linhas; a visibilidade é decidida
  // apenas no nível do grupo (as linhas não são filtradas no provider)
  const { statusFinal, errorCount } = useMemo(
    () => getProcessGroupStatus(rowsMap),
    [rowsMap]
  )

  const isVisible = useMemo(
    () =>
      isProcessStatusVisible(statusFinal, {
        iniciadoCheck,
        atencaoCheck,
        noprazoCheck,
        atrasadoCheck,
        aguardandoAprovacaoCheck,
        recusadoCheck
      }),
    [
      statusFinal,
      iniciadoCheck,
      atencaoCheck,
      noprazoCheck,
      atrasadoCheck,
      aguardandoAprovacaoCheck,
      recusadoCheck
    ]
  )

  const getProceText = (proceCode: any) => {
    switch (proceCode) {
      case '103':
        return 'COMPRA'
      case '105':
        return 'DISTRIBUIÇÃO'
      case '108':
        return 'PROGRAMADA'
      case '102':
        return 'REAB'
      default:
        return 'Status Desconhecido'
    }
  }

  const renderStatusIcon = (status: string | number, errorCount = 0) => {
    const iconBox = (children: ReactNode) => (
      <Box
        display="inline-block"
        position="relative"
        zIndex={1}
        maxHeight="100%"
      >
        {children}
        {status === '3' && errorCount > 0 && (
          <Box
            as="span"
            position="absolute"
            top="-4px"
            right="-8px"
            background="red.500"
            color="white"
            fontSize="8px"
            px="1"
            borderRadius="full"
            fontWeight="semibold"
            lineHeight="1"
            minW="14px"
            h="14px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {errorCount}
          </Box>
        )}
      </Box>
    )

    const config = STATUS_CONFIG[String(status)]
    if (!config) return null

    return (
      <Tooltip
        label={config.label}
        placement="top"
        bg="gray.700"
        color="white"
        fontSize="xs"
        hasArrow
        portalProps={{ appendToParentPortal: false }}
      >
        {iconBox(config.icon)}
      </Tooltip>
    )
  }

  return (
    <>
      {isVisible && firstObject && (
        <>
          <tr
            {...attributes}
            className="custom-display"
            style={{ overflow: 'hidden' }}
          >
            <td align="center" valign="top" className="tr-gray"></td>
            <td
              align="center"
              valign="top"
              className="tr-gray"
              style={{ cursor: 'pointer' }}
              onClick={onToggle}
            >
              {isOpen ? (
                <AiOutlineEyeInvisible size={20} />
              ) : (
                <AiOutlineEye size={20} />
              )}
            </td>
            <td colSpan={3} className="tr-gray">
              <b>
                {firstObject['info-id']} - {firstObject['info-cria']} -{' '}
                {getProceText(firstObject['info-proce'])} -{' '}
                {firstObject['info-user']} - ({count})
              </b>
            </td>
            <td
              className="tr-gray"
              align="center"
              style={{ overflow: 'hidden', position: 'sticky' }}
            >
              {renderStatusIcon(statusFinal, errorCount)}
            </td>

            <td className="tr-gray" align="center"></td>
            <td colSpan={7} className="tr-gray"></td>
          </tr>

          {isOpen &&
            (rowsMap.length === 0 ? (
              <tr>
                <td colSpan={12} align="center">
                  Dados não encontrados
                </td>
              </tr>
            ) : (
              <>
                {rowsMap.map((row: any, key: number) => (
                  <TableGridItemAlertFollowUp
                    data={row}
                    key={`table-grid-${key + 1}`}
                    indexData={key}
                  />
                ))}
              </>
            ))}
        </>
      )}
    </>
  )
}

export default TableGridCollpsePurchaseProcess
